import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Eye, EyeOff, Map, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { logout, registerUser } from '@/redux/slices/auth/authSlice';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LocationPickerModal } from './LocationPickerModal';
import { setAccessToken } from '@/api/tokenService';
import GlobalLoader from './ui/GlobalLoader';
import { ShiftTiming } from '@/redux/slices/auth/authSlice';
// import courierLogo from 'figma:asset/fad5c0c98971d01ba354f23784cde20752b79528.png';

interface SignupProps {
  onSignup: () => void;
  onNavigateToLogin: () => void;
}

export function Signup({ onSignup, onNavigateToLogin }: SignupProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<{
    pharmacyName: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    location: {
      latitude: number;
      longitude: number;
      address: string;
    } | string;
    password: string;
    confirmPassword: string;
    shiftTimings: ShiftTiming[];
  }>({
    pharmacyName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    password: '',
    confirmPassword: '',
    shiftTimings: [
      { day: 'Monday', isOpen: true, openingTime: '11:00', closingTime: '19:00' },
      { day: 'Tuesday', isOpen: true, openingTime: '11:00', closingTime: '19:00' },
      { day: 'Wednesday', isOpen: true, openingTime: '11:00', closingTime: '19:00' },
      { day: 'Thursday', isOpen: true, openingTime: '11:00', closingTime: '19:00' },
      { day: 'Friday', isOpen: true, openingTime: '11:00', closingTime: '19:00' },
      { day: 'Saturday', isOpen: true, openingTime: '10:00', closingTime: '18:00' },
      { day: 'Sunday', isOpen: false, openingTime: '00:00', closingTime: '00:00' }
    ],
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<[number, number] | null>([40.0583, -74.4057]); // Default to New Jersey
  const [tempAddress, setTempAddress] = useState('Trenton, New Jersey, USA');
  const baseURL = import.meta.env.VITE_API_BASE_URL;

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleShiftTimingChange = (day: string, field: 'isOpen' | 'openingTime' | 'closingTime', value: boolean | string) => {
    setFormData(prev => ({
      ...prev,
      shiftTimings: prev.shiftTimings.map(timing => 
        timing.day === day 
          ? { ...timing, [field]: value }
          : timing
      )
    }));
  };

  const handleMapModalOpen = () => {
    setIsMapModalOpen(true);
  };

  const handleLocationConfirm = (location: { latitude: number; longitude: number; address: string }) => {
    setFormData(prev => ({ ...prev, location }));
    setSelectedPosition([location.latitude, location.longitude]);
    setTempAddress(location.address);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match')
      return;
    }

    try {
      setIsLoading(true);

      // Send location as is - backend will handle GeoJSON transformation
      console.log('Sending registration data:', formData);
      const res = await dispatch<any>(registerUser(formData)).unwrap();
      
      setFormData({
        pharmacyName: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        location: '',
        password: '',
        confirmPassword: '',
        shiftTimings: [
          { day: 'Monday', isOpen: true, openingTime: '11:00', closingTime: '19:00' },
          { day: 'Tuesday', isOpen: true, openingTime: '11:00', closingTime: '19:00' },
          { day: 'Wednesday', isOpen: true, openingTime: '11:00', closingTime: '19:00' },
          { day: 'Thursday', isOpen: true, openingTime: '11:00', closingTime: '19:00' },
          { day: 'Friday', isOpen: true, openingTime: '11:00', closingTime: '19:00' },
          { day: 'Saturday', isOpen: true, openingTime: '10:00', closingTime: '18:00' },
          { day: 'Sunday', isOpen: false, openingTime: '00:00', closingTime: '00:00' }
        ],
      });
      setSelectedPosition(null);
      setTempAddress('');
      navigate("/auth/login");
    } catch (error) {
      if(axios.isAxiosError && error.response) {
        // alert(error.response.data?.message || "Incorrect credentials.");
        toast.error(error.response.data?.message || "Incorrect credentials.");
      }
    } finally {  
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header with Back to Home */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="outline"
            onClick={() => navigate("/")}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Button>
          
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-gray-900">Create Your Account</h1>
            <p className="text-gray-600 mt-2">Join Courier-RX delivery network</p>
          </div>
          
          <div className="w-24"></div> {/* Spacer for centering */}
        </div>
        <Card className="shadow-xl border-0">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-center text-xl">Pharmacy Registration</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Pharmacy Information */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Pharmacy Information</h3>
                <div className="space-y-2">
                  <Label htmlFor="pharmacyName">Pharmacy Name</Label>
                  <Input
                    id="pharmacyName"
                    placeholder="Enter pharmacy name"
                    value={formData.pharmacyName}
                    onChange={(e) => handleInputChange('pharmacyName', e.target.value)}
                    required
                    className="h-11"
                  />
                </div>
              </div>

              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Personal Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      placeholder="Enter first name"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      placeholder="Enter last name"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter email address"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                </div>
              </div>

              {/* Shift Timings */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Operating Hours</h3>
                <p className="text-sm text-gray-600">Set your pharmacy's operating hours for each day of the week</p>
                <div className="space-y-3">
                  {formData.shiftTimings.map((timing) => (
                    <div key={timing.day} className="flex items-center space-x-4 p-3 border border-gray-200 rounded-lg">
                      <div className="w-20 text-sm font-medium text-gray-700">
                        {timing.day}
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`${timing.day}-open`}
                          checked={timing.isOpen}
                          onChange={(e) => handleShiftTimingChange(timing.day, 'isOpen', e.target.checked)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <label htmlFor={`${timing.day}-open`} className="text-sm text-gray-700">
                          Open
                        </label>
                      </div>

                      {timing.isOpen ? (
                        <div className="flex items-center space-x-2">
                          <Input
                            type="time"
                            value={timing.openingTime}
                            onChange={(e) => handleShiftTimingChange(timing.day, 'openingTime', e.target.value)}
                            className="h-8 w-32 text-sm"
                          />
                          <span className="text-sm text-gray-500">to</span>
                          <Input
                            type="time"
                            value={timing.closingTime}
                            onChange={(e) => handleShiftTimingChange(timing.day, 'closingTime', e.target.value)}
                            className="h-8 w-32 text-sm"
                          />
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 font-medium">Closed</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Location Information */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Location Information</h3>
                <div className="space-y-2">
                  <Label htmlFor="location">Pharmacy Location</Label>
                  <div className="flex gap-2">
                    <Input
                      id="location"
                      value={
                        typeof formData.location === 'string' 
                          ? formData.location 
                          : formData.location.address
                      }
                      placeholder="Click map icon to select location"
                      readOnly
                      required
                      className="flex-1 h-11"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (typeof formData.location !== 'string' && formData.location.latitude) {
                          setSelectedPosition([formData.location.latitude, formData.location.longitude]);
                          setTempAddress(formData.location.address);
                        } else {
                          setSelectedPosition(null);
                          setTempAddress('');
                        }
                        handleMapModalOpen();
                      }}
                      className="shrink-0 h-11"
                    >
                      <Map className="w-4 h-4 mr-2" />
                      Select on Map
                    </Button>
                  </div>
                  {typeof formData.location !== 'string' && formData.location.latitude && (
                    <p className="text-xs text-gray-500 mt-1">
                      📍 Coordinates: {formData.location.latitude.toFixed(6)}, {formData.location.longitude.toFixed(6)}
                    </p>
                  )}
                </div>
              </div>

              {/* Password */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Security</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Create password"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        required
                        className="h-11 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm password"
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        required
                        className="h-11 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* Terms */}
              <div className="flex items-start space-x-2">
                <input type="checkbox" required className="w-4 h-4 mt-1 text-green-600 rounded focus:ring-green-500" />
                <label className="text-sm text-gray-600">
                  I agree to the{' '}
                  <a href="#" className="text-green-600 hover:text-green-700 font-medium">Terms of Service</a>
                  {' '}and{' '}
                  <a href="#" className="text-green-600 hover:text-green-700 font-medium">Privacy Policy</a>
                </label>
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-green-600 hover:bg-green-700 text-white"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Creating Account...
                  </div>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>
            <Separator className="my-6" />
            <div className="text-center">
              <p className="text-gray-600">
                Already have an account?{' '}
                <button
                  onClick={onNavigateToLogin}
                  className="text-green-600 hover:text-green-700 font-medium"
                >
                  Sign in
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
        <div className="text-center mt-6">
          <p className="text-xs text-gray-500">
            © 2025 Courier-RX. All rights reserved.
          </p>
        </div>
      </div>

      {/* Location Picker Modal */}
      <LocationPickerModal
        isOpen={isMapModalOpen}
        onClose={() => setIsMapModalOpen(false)}
        onConfirm={handleLocationConfirm}
        initialPosition={selectedPosition}
        initialAddress={tempAddress}
      />

      {/* Global loading overlay */}
      <GlobalLoader 
        isLoading={isLoading}
        message="Creating account..."
        subMessage="Please wait while we create your account"
      />
    </div>
  );
}